import React, { forwardRef, ReactNode, useRef, useState } from 'react';
import {
  Select as SelectUI,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '@/src/utils';
import LucideIcon from '../Icons/LucideIcon';
import LocalIcon from '../Icons/LocalIcon';
import ClearSvg from '@/src/assets/icons/icon-input-clear.svg';
import Typo, { typoColorData, typoTypeData } from '../Typo';
import { ScrollArea } from '../../ui/scroll-area';

export interface SelectOption {
  label: ReactNode;
  value: string;
  itemProps?: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>;
}

export interface SelectProps {
  option?: {
    optionList: SelectOption[];
    selectLabel?: ReactNode;
    selectLabelProps?: React.ComponentPropsWithoutRef<
      typeof SelectPrimitive.Label
    >;
    groupProps?: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Group>;
  }[];

  triggerProps?: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>;
  contentProps?: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>;
  valueProps?: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>;
  rootProps?: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Select>;
  allowClear?: boolean;
  scrollAreaClassName?: string;

  valueTypoType?: keyof typeof typoTypeData;
  valueTypoColor?: keyof typeof typoColorData;
  placeholderTypoType?: keyof typeof typoTypeData;
  placeholderTypoColor?: keyof typeof typoColorData;
}

const Select = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectProps
>(
  (
    {
      triggerProps,
      contentProps,
      valueProps,
      option,
      rootProps,
      allowClear = false,
      scrollAreaClassName,
      valueTypoType,
      valueTypoColor,
      placeholderTypoType,
      placeholderTypoColor,
    },
    ref,
  ) => {
    const triggerRef = useRef<HTMLButtonElement>();
    const [isHover, setIsHover] = useState(false);

    const renderRightIcon = () => {
      if (
        allowClear &&
        isHover &&
        rootProps?.value &&
        !triggerProps?.disabled
      ) {
        return (
          <LocalIcon
            iconSrc={ClearSvg}
            iconSize={16}
            onPointerDown={e => {
              e.stopPropagation();
              rootProps?.onValueChange?.('');
            }}
            className="flex flex-shrink-0"
          />
        );
      }

      return (
        <LucideIcon
          name="ChevronDown"
          size={16}
          className="flex flex-shrink-0 opacity-50"
        />
      );
    };

    return (
      <SelectUI {...rootProps}>
        <SelectTrigger
          ref={node => {
            if (node) {
              triggerRef.current = node;
            }
            if (ref) {
              if (typeof ref === 'function') {
                ref(node);
              } else {
                ref.current = node;
              }
            }
          }}
          onMouseOver={() => {
            setIsHover(true);
          }}
          onMouseLeave={() => {
            setIsHover(false);
          }}
          {...triggerProps}
          className={cn(
            'flex overflow-hidden border bg-white focus:border-gray9 focus:ring-0 focus:ring-offset-0 focus-visible:border-gray9 focus-visible:ring-0 focus-visible:ring-offset-0 disabled:border-gray3 disabled:bg-gray1 disabled:text-gray6',
            triggerProps?.className,
          )}
        >
          {rootProps?.value === undefined || rootProps?.value === '' ? (
            <Typo
              typoType={placeholderTypoType ?? 'b16r'}
              typoColor={placeholderTypoColor ?? 'gray4'}
              className={cn(valueProps?.className)}
            >
              {valueProps?.placeholder || 'Select'}
            </Typo>
          ) : (
            <Typo
              typoType={valueTypoType ?? 'b16r'}
              typoColor={valueTypoColor}
              className={cn(valueProps?.className)}
            >
              <SelectValue {...valueProps} />
            </Typo>
          )}

          <SelectPrimitive.Icon className="leading-[9px]">
            {renderRightIcon()}
          </SelectPrimitive.Icon>
        </SelectTrigger>

        <SelectContent
          side="bottom"
          position="popper"
          className={cn('z-select', contentProps?.className)}
          {...contentProps}
        >
          <ScrollArea
            className={cn(
              'flex max-h-[300px] flex-col items-center',
              scrollAreaClassName,
            )}
          >
            {option?.map(
              (
                { selectLabel, selectLabelProps, groupProps, optionList },
                idx,
              ) => {
                return (
                  <SelectGroup {...groupProps} key={idx.toString()}>
                    {selectLabel && (
                      <SelectLabel {...selectLabelProps}>
                        {selectLabel}
                      </SelectLabel>
                    )}

                    {optionList.map(({ value, label, itemProps }, itemIdx) => {
                      return (
                        <SelectItem
                          key={itemIdx.toString()}
                          value={value}
                          {...itemProps}
                        >
                          {label}
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                );
              },
            )}
          </ScrollArea>
        </SelectContent>
      </SelectUI>
    );
  },
);

export default Select;
